import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Checkbox } from '../../../../components/inputs/Checkbox';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { repositoryClassificationsApi } from '../classificationsApi';
import type { RepositoryClassificationListItem } from '../repositoryTypes';
import { sampleClassification } from '../utils/sampleClassification';

/**
 * Create or edit one information classification.
 *
 * A name, its definition, and whether it is confidential. **No order field** — a position is
 * assigned by the server on create and left alone on an edit: the matrix reads least to most
 * restricted, and a rename is not a reorder.
 */
export function ClassificationModal({
  open,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Null creates. */
  editing: RepositoryClassificationListItem | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  // Seeded once, on mount. The parent mounts this only while it is open.
  const [name, setName] = useState(editing?.name ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');
  const [isConfidential, setIsConfidential] = useState(editing?.isConfidential ?? false);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const valid = name.trim().length > 0;
  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = {
      name: name.trim(),
      description: description.trim() || null,
      isConfidential,
    };

    try {
      if (editing) await repositoryClassificationsApi.update(editing.id, request);
      else await repositoryClassificationsApi.create(request);

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not save the classification.', 0);

      setError(failure);

      // Say which field, show it, and put focus on it.
      focusValidationError(failure, { idPrefix: 'repository-classification' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit classification' : 'New classification'}
      size="lg"
      id="repository-classification-modal"
      autogenerate={{
        onGenerate: () => {
          const generated = sampleClassification();
          setName(generated.name);
          setDescription(generated.description);
          // The confidential flag stays at its default — see sampleClassification.
        },
        disabled: saving,
      }}
      confirm={{
        id: 'repository-classification-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button
            id="repository-classification-cancel"
            variant="secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            id="repository-classification-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <TextField
          id="repository-classification-name"
          label="Name"
          required
          description="What the classification is called, e.g. Company Confidential. Held once among the live rows, and shown on every document carrying it."
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={100}
          disabled={saving}
        />

        <TextField
          id="repository-classification-description"
          label="Definition"
          description="What the classification means, in this organization's own words. Read by the clerk choosing one at the moment of registering a document, not only by whoever maintains this list."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={1000}
          disabled={saving}
        />

        {/*
          A rule rather than a label — and the help text says which rule, because the word
          "confidential" alone would leave a reader guessing whether it changes anything.
        */}
        <label
          htmlFor="repository-classification-confidential"
          className="flex items-start gap-2 text-sm text-slate-600"
        >
          <Checkbox
            id="repository-classification-confidential"
            name="repository-classification-confidential"
            checked={isConfidential}
            onChange={() => setIsConfidential((v) => !v)}
            disabled={saving}
          />
          <span>
            Documents under this classification are confidential.
            <span className="block text-xs text-slate-500">
              A confidential document is refused into a folder shared beyond the people
              explicitly granted it. In the source matrix this is on for the two Confidential
              tiers and off for Internal.
            </span>
          </span>
        </label>
      </div>
    </Modal>
  );
}
